import { DicomData } from 'app/interfaces/Dicom';
import { AcquisitionConstants } from 'app/interfaces/Image';
import ObjectIdGenerator from 'app/interfaces/ObjectIdGenerator';
import { FindQueryModel, GetQueryModel, PACSCommunication } from 'app/interfaces/PACSCommunication';
import { Study } from 'app/interfaces/Study';
import { StudyStore } from 'app/interfaces/StudyStore';
import logger from 'app/utils/debug/logger';
import { getDicomValue } from 'app/utils/dicom/DicomData';
import extractAcquisitionDataFromDicomData from 'app/utils/dicom/extractAcquisitionDataFromDicomData';
import _ from 'lodash';

type ImportDicomStudyDependencies = {
  pacsCommunication: PACSCommunication;
  studyStore: StudyStore;
  objectIdGenerator: ObjectIdGenerator;
};

export default async function importDicomStudy(
  studyInstanceUID: string,
  dependencies: ImportDicomStudyDependencies
): Promise<Study | undefined> {
  try {
    const { pacsCommunication, studyStore, objectIdGenerator } = dependencies;

    let studyId = objectIdGenerator.create();

    const existingStudiesPromise = studyStore.filterStudies({
      studyInstanceUIDs: [studyInstanceUID],
    });
    logger.debug('[importDicomStudy]studyInstanceUID', studyInstanceUID);
    const dicomDataSet = (await pacsCommunication.find(
      {
        QueryRetrieveLevel: 'IMAGE',
        StudyInstanceUID: studyInstanceUID,
        SOPInstanceUID: undefined,
        Modality: undefined,
        StudyTime: undefined,
        StudyDate: undefined,
      },
      FindQueryModel.Study
    )) as DicomData[];

    logger.debug('[importDicomStudy]dicomDataSet', dicomDataSet);

    const compatibleImages = dicomDataSet.filter(
      (dicomData) =>
        getDicomValue(dicomData.Modality) === 'DX' || getDicomValue(dicomData.Modality) === 'CR'
    );

    if (compatibleImages.length === 0) {
      return undefined;
    }
    const { patient, studyTime } = extractAcquisitionDataFromDicomData(compatibleImages[0]);

    const existingStudy = (await existingStudiesPromise)[0];

    if (existingStudy) {
      studyId = existingStudy._id;
      if (_.some(existingStudy.images, { origin: 'acquisition' })) {
        return existingStudy;
      }
    } else {
      studyStore.updateStudyMetadata(studyId, {
        creationDate: studyTime,
        study_instance_uid: studyInstanceUID,
      });
    }
    studyStore.updatePatient(studyId, patient);

    const existingImagesUids = existingStudy?.images.map((image) => image.sop_instance_uid) ?? [];
    for (const dicomData of compatibleImages) {
      const imageUid = getDicomValue(dicomData.SOPInstanceUID);
      if (existingImagesUids.includes(imageUid)) {
        continue;
      }
      const imageId = objectIdGenerator.create();
      try {
        await studyStore.updateImage(studyId, imageId, {
          origin: 'PACS',
          sop_instance_uid: imageUid,
        });
      } catch (error) {
        console.error('[importDicomStudy] updateImage', error);
        throw error;
      }
    }
    return studyStore.getStudy(studyId);
  } catch (error) {
    console.error('[importDicomStudy] error', error);
    throw error;
  }
}
